import React, { useEffect, useState, useRef } from "react";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import FormInput from "../input";
import DropdownMenu from "../dropdown";
import Button from "../button";
//import { getCitiesByState, nigeriaStates } from "@/utils/location";
//import SearchableDropdown from "../search-dropdown";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";
import {
  ListingType,
  PropertyCategory,
  PropertyTypeLabels,
  CategoryPropertyTypeConfig,
} from "@/types/property-config";
import PropertyResultsHeader from "./FilterQueryRender";
import { useCurrency } from "@/hooks/useCurrency";
import { searchQueryPlaceholder } from "@/components/pages/home/filter/HomeFilter";

export function getPropertyType(object: any, value: any) {
  return Object.keys(object).find((key) => object[key] === value);
}

const formatPriceWithCommas = (
  value: string | string[] | undefined,
): string => {
  const raw =
    typeof value === "string"
      ? value
      : Array.isArray(value)
        ? (value[0] ?? "")
        : "";
  const cleaned = raw.replace(/[^\d]/g, "");
  if (!cleaned) return "";
  return Number(cleaned).toLocaleString("en-US");
};

const stripCommas = (value: string): string => value.replace(/,/g, "");

const LISTING_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: ListingType.SALE, label: "Buy" },
  { value: ListingType.RENT, label: "Rent" },
  { value: ListingType.SHORTLET, label: "Shortlet" },
  { value: ListingType.LEASE, label: "Lease" },
];

const LISTING_TYPE_LABELS: Record<string, string> = {
  [ListingType.SALE]: "Buy",
  [ListingType.RENT]: "Rent",
  [ListingType.SHORTLET]: "Shortlet",
  [ListingType.LEASE]: "Lease",
};

const PROPERTY_CATEGORY_LABELS: Record<string, string> = {
  [PropertyCategory.RESIDENTIAL]: "Residential",
  [PropertyCategory.COMMERCIAL]: "Commercial",
  [PropertyCategory.INDUSTRIAL]: "Industrial",
  [PropertyCategory.LAND]: "Land",
  [PropertyCategory.SPECIAL_PURPOSE]: "Special Purpose",
};

interface ModernMinimalistPropertyFilterProps {
  onApply?: (params: Record<string, string>) => void;
}

const ModernMinimalistPropertyFilter = ({
  onApply,
}: ModernMinimalistPropertyFilterProps) => {
  const router = useRouter();
  const { selectedCurrency } = useCurrency();
  // const [cityOptions, setCityOptions] = useState<string[]>([]);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const {
    location,
    searchQuery,
    shared,
    serviced,
    addedToSite,
    //   state,
    //   city,
    bedroomCount,
    bathroomCount,
    listingType,
    realtorId,
    furnished,
    propertyType,
    propertyCategory,
    minPrice,
    maxPrice,
  } = router.query;

  // const [selectedState, setSelectedState] = useState<any>(state || null);

  const [filters, setFilters] = useState({
    searchQuery: searchQuery || "",
    location: location || "Lagos",
    serviced: serviced === "true" ? "Yes" : "",
    shared: shared === "true" ? "Yes" : "",
    addedToSite: addedToSite || "",
    //   state: state || "",
    //  city: city || "",
    bedroomCount: bedroomCount || "",
    bathroomCount: bathroomCount || "",
    listingType: listingType || "",
    propertyType: propertyType || "",
    propertyCategory: propertyCategory || "",
    minPrice: minPrice || "",
    maxPrice: maxPrice || "",
    furnished: furnished === "true" ? "Yes" : "",
  });
  /*


  useEffect(() => {
    if (selectedState) {
      const cities = getCitiesByState(selectedState);
      setCityOptions(cities);
    } else {
      setCityOptions([]);
    }
  }, [selectedState]);
*/

  useEffect(() => {
    setFilters({
      searchQuery: searchQuery || "",
      location: location || "",
      serviced: serviced === "true" ? "Yes" : "",
      shared: shared === "true" ? "Yes" : "",
      addedToSite: addedToSite || "",
      //  state: state || "",
      // city: city || "",
      bedroomCount: bedroomCount || "",
      bathroomCount: bathroomCount || "",
      listingType: listingType || "",
      propertyType: propertyType || "",
      propertyCategory: propertyCategory || "",
      minPrice: minPrice || "",
      maxPrice: maxPrice || "",
      furnished: furnished === "true" ? "Yes" : "",
    });
  }, [router.query]);

  const handleFilterChange = (key: string, value: string | boolean) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      searchQuery: "",
    }));
  };

  const handleSearchChange = (key: string, value: string | boolean) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleListingTypeChange = (selectedLabel: string) => {
    const apiValue =
      selectedLabel === "Any"
        ? ""
        : selectedLabel === "Buy"
          ? ListingType.SALE
          : selectedLabel.toLowerCase();
    handleFilterChange("listingType", apiValue);
  };

  const handlePropertyCategoryChange = (selectedLabel: string) => {
    const apiValue =
      selectedLabel === "Any"
        ? ""
        : Object.entries(PROPERTY_CATEGORY_LABELS).find(
            ([, label]) => label === selectedLabel,
          )?.[0] || "";
    handleFilterChange("propertyCategory", apiValue);
    if (apiValue) handleFilterChange("propertyType", "");
  };

  const handlePropertyTypeChange = (selectedLabel: string) => {
    const apiValue =
      selectedLabel === "Any"
        ? ""
        : Object.entries(PropertyTypeLabels).find(
            ([, label]) => label === selectedLabel,
          )?.[0] || "";
    handleFilterChange("propertyType", apiValue);
  };

  const getPropertyTypeOptions = (category: string) => {
    if (category) {
      const types =
        CategoryPropertyTypeConfig[category as PropertyCategory] || [];
      return ["Any", ...types.map((type) => PropertyTypeLabels[type])];
    }
    return ["Any", ...Object.values(PropertyTypeLabels)];
  };

  const applyFilters = (e?: React.FormEvent) => {
    e?.preventDefault();
    const params: Record<string, string> = {};

    // If a search query exists, send only that
    let searchQuery = filters.searchQuery as string;
    searchQuery = searchQuery?.trim();
    if (searchQuery) {
      params.searchQuery = searchQuery;
    } else {
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "Any") {
          let finalValue = value;

          if (value === "Yes") {
            finalValue = "true";
          } else if (value === "No") {
            finalValue = "false";
          }

          params[key] =
            key === "propertyCategory"
              ? finalValue?.toString()
              : finalValue?.toString()?.replace(/,/g, "")?.toLowerCase();
        }
      });

      if (realtorId) {
        params.realtorId = realtorId.toString();
      }
    }

    onApply?.(params);

    router.push({
      pathname: router.pathname,
      query: params,
    });

    setIsMobileOpen(false);
    setShowAdvanced(false);
  };

  const clearFilters = () => {
    setFilters({
      searchQuery: "",
      shared: "",
      addedToSite: "Any",
      serviced: "",
      location: "Any",
      // state: "Any",
      //  city: "Any",
      bedroomCount: "Any",
      bathroomCount: "Any",
      listingType: "Any",
      propertyType: "Any",
      propertyCategory: "Any",
      minPrice: "Any",
      maxPrice: "Any",
      furnished: "",
    });

    const params: Record<string, string> = {};

    if (realtorId) {
      params.realtorId = realtorId.toString();
      router.push({
        pathname: router.pathname,
        query: { realtorId },
      });
    } else {
      router.push(router.pathname);
    }

    onApply?.(params);
  };

  const libraries: "places"[] = ["places"];
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY as string,
    libraries,
  });

  return (
    <div className="w-full">
      {/* ========================================== */}
      {/* DESKTOP VIEW                               */}
      {/* ========================================== */}
      <div className="hidden md:block w-full bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm">
        {/* Unified Main Search Bar (Matches layout directly) */}
        <div className="flex items-center gap-3">
          {/* Main search input box */}
          <form
            onSubmit={applyFilters}
            className="flex-1 flex items-center gap-2 px-4 h-[50px] bg-slate-50 border border-slate-200/60 rounded-xl focus-within:ring-1 focus-within:ring-bnp-cyan focus-within:border-bnp-cyan transition-all"
          >
            <input
              type="text"
              placeholder={searchQueryPlaceholder}
              value={filters.searchQuery}
              onChange={(e) =>
                handleSearchChange("searchQuery", e.target.value)
              }
              className="w-full bg-transparent text-slate-800 text-sm placeholder-slate-400 focus:outline-none"
            />
          </form>

          {/* Filled Teal Search Action Button */}
          <button
            onClick={applyFilters}
            className="h-[50px] px-6 flex items-center gap-2 bg-bnp-cyan hover:bg-[#247A8A] text-white rounded-xl active:scale-98 transition-all text-sm font-semibold shadow-sm"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <span>search</span>
          </button>

          {/* Apply Filter Button (Accordion Trigger on Desktop) */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`w-max px-10 h-[50px] flex items-center justify-center border rounded-xl transition-all active:scale-95 ${
              showAdvanced
                ? "bg-bnp-teal border-bnp-cyan text-bnp-cyan"
                : "bg-slate-50 border-slate-200 hover:bg-slate-100 text-slate-700"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <line x1="4" y1="21" x2="4" y2="14" />
              <line x1="4" y1="10" x2="4" y2="3" />
              <line x1="12" y1="21" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12" y2="3" />
              <line x1="20" y1="21" x2="20" y2="16" />
              <line x1="20" y1="12" x2="20" y2="3" />
              <line x1="1" y1="14" x2="7" y2="14" />
              <line x1="9" y1="8" x2="15" y2="8" />
              <line x1="17" y1="16" x2="23" y2="16" />
            </svg>
            <span className="hidden sm:block pl-2">Filters</span>
          </button>
        </div>

        {/* Desktop Dropdown Filters (Revealed via Accordion style) */}
        <div
          className={`transition-all duration-300 ease-in-out ${
            showAdvanced
              ? "max-h-[800px] mt-6 pt-5 border-t border-slate-100 opacity-100"
              : "max-h-0 invisible"
          }`}
        >
          <div className="grid grid-cols-4 gap-4">
            {/* Location (Autocomplete map feature) */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Location
              </label>
              {isLoaded ? (
                <Autocomplete
                  options={{
                    componentRestrictions: { country: "NG" },
                    fields: ["name"],
                    types: ["(cities)"],
                  }}
                  onLoad={(autocomplete) => {
                    autocompleteRef.current = autocomplete;
                  }}
                  onPlaceChanged={() => {
                    const place = autocompleteRef.current?.getPlace();
                    if (place?.name)
                      handleFilterChange("location", place?.name);
                  }}
                >
                  <input
                    type="text"
                    placeholder="Enter City"
                    value={filters.location === "Any" ? "" : filters.location}
                    onChange={(e) =>
                      handleFilterChange("location", e.target.value)
                    }
                    className="w-full h-[46px] px-3.5 bg-slate-50/50 border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none text-sm"
                  />
                </Autocomplete>
              ) : (
                <input
                  type="text"
                  disabled
                  placeholder="Loading maps..."
                  className="w-full h-[46px] px-3.5 bg-slate-100 border border-slate-200 rounded-lg text-slate-400 text-sm"
                />
              )}
            </div>

            {/* Listing Type Dropdown */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Listing Type
              </label>
              <DropdownMenu
                name="listingType"
                value={
                  filters.listingType
                    ? LISTING_TYPE_LABELS[filters.listingType as string] ||
                      capitalizeFirstLetter(filters.listingType as string)
                    : "Any"
                }
                onChange={(e) => handleListingTypeChange(e.target.value)}
                options={["Any", ...LISTING_TYPE_OPTIONS.map((o) => o.label)]}
              />
            </div>

            {/* Property Category Dropdown */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Property Category
              </label>
              <DropdownMenu
                name="propertyCategory"
                value={
                  filters.propertyCategory
                    ? PROPERTY_CATEGORY_LABELS[
                        filters.propertyCategory as string
                      ] || filters.propertyCategory
                    : "Any"
                }
                onChange={(e) => handlePropertyCategoryChange(e.target.value)}
                options={["Any", ...Object.values(PROPERTY_CATEGORY_LABELS)]}
              />
            </div>

            {/* Property Type Dropdown */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Property Type
              </label>
              <DropdownMenu
                name="propertyType"
                value={
                  filters.propertyType
                    ? PropertyTypeLabels[
                        filters.propertyType as keyof typeof PropertyTypeLabels
                      ] || filters.propertyType
                    : "Any"
                }
                onChange={(e) => handlePropertyTypeChange(e.target.value)}
                options={getPropertyTypeOptions(
                  filters.propertyCategory as string,
                )}
              />
            </div>
          </div>
          {/*
  
  
          <div className="grid grid-cols-4 gap-4 mt-4">

            <div className="flex flex-col gap-1.5" style={{ display: "none" }}>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                State
              </label>
              <SearchableDropdown
                name="state"
                options={["Any", ...nigeriaStates]}
                value={filters.state || "Any"}
                onChange={(e) => {
                  handleFilterChange(
                    "state",
                    e.target.value === "Any" ? "" : e.target.value,
                  );
                  setSelectedState(
                    e.target.value === "Any" ? null : e.target.value,
                  );
                  handleFilterChange("city", "Any");
                }}
              />
            </div>

            <div className="flex flex-col gap-1.5" style={{ display: "none" }}>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                City
              </label>
              <SearchableDropdown
                name="city"
                value={filters.city || "Any"}
                options={["Any", ...cityOptions]}
                onChange={(e) =>
                  handleFilterChange(
                    "city",
                    e.target.value === "Any" ? "" : e.target.value,
                  )
                }
                disabled={!selectedState}
              />
            </div>
          </div>
  
  */}

          <div className="grid grid-cols-4 gap-4 mt-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Min. Price
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Any"
                value={formatPriceWithCommas(filters.minPrice || "")}
                onChange={(e) =>
                  handleFilterChange(
                    "minPrice",
                    formatPriceWithCommas(e.target.value),
                  )
                }
                className="w-full h-[46px] px-3 bg-slate-50/50 border border-slate-200 rounded-lg text-slate-800 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Max. Price
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Any"
                value={formatPriceWithCommas(filters.maxPrice || "")}
                onChange={(e) =>
                  handleFilterChange(
                    "maxPrice",
                    formatPriceWithCommas(e.target.value),
                  )
                }
                className="w-full h-[46px] px-3 bg-slate-50/50 border border-slate-200 rounded-lg text-slate-800 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Serviced
              </label>
              <DropdownMenu
                name="serviced"
                value={filters.serviced || "Any"}
                onChange={(e) =>
                  handleFilterChange(
                    "serviced",
                    e.target.value === "Any" ? "" : e.target.value,
                  )
                }
                options={["Any", "Yes", "No"]}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Shared
              </label>
              <DropdownMenu
                name="shared"
                value={filters.shared || "Any"}
                onChange={(e) =>
                  handleFilterChange(
                    "shared",
                    e.target.value === "Any" ? "" : e.target.value,
                  )
                }
                options={["Any", "Yes", "No"]}
              />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4 mt-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Furnished
              </label>
              <DropdownMenu
                name="furnished"
                value={filters.furnished || "Any"}
                onChange={(e) =>
                  handleFilterChange(
                    "furnished",
                    e.target.value === "Any" ? "" : e.target.value,
                  )
                }
                options={["Any", "Yes", "No"]}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Bedrooms
              </label>
              <DropdownMenu
                name="bedroomCount"
                options={[
                  "Any",
                  "1",
                  "2",
                  "3",
                  "4",
                  "5",
                  "6",
                  "7",
                  "8",
                  "9",
                  "10",
                  "10+",
                ]}
                value={filters.bedroomCount}
                onChange={(e) =>
                  handleFilterChange("bedroomCount", e.target.value)
                }
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Bathrooms
              </label>
              <DropdownMenu
                name="bathroomCount"
                options={[
                  "Any",
                  "1",
                  "2",
                  "3",
                  "4",
                  "5",
                  "6",
                  "7",
                  "8",
                  "9",
                  "10",
                  "10+",
                ]}
                value={filters.bathroomCount}
                onChange={(e) =>
                  handleFilterChange("bathroomCount", e.target.value)
                }
              />
            </div>
          </div>

          {/* Action Row inside Desktop Accordion */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm font-semibold text-slate-400 hover:text-slate-600 transition-all"
            >
              Reset settings
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowAdvanced(false)}
                className="px-4 py-2 border border-slate-200 text-sm font-semibold text-slate-600 rounded-lg hover:bg-slate-50"
              >
                Close
              </button>
              <Button
                onClick={applyFilters}
                className="bg-bnp-cyan hover:bg-[#247A8A] text-white font-sans text-sm font-semibold h-[38px] px-5 rounded-lg transition-all"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================== */}
      {/* MOBILE VIEW                                */}
      {/* ========================================== */}
      <div className="md:hidden w-full flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-2 shadow-sm">
        {/* Responsive Text search box input */}
        <form
          onSubmit={applyFilters}
          className="flex-1 flex items-center gap-2 px-3 h-[46px] bg-slate-50/80 rounded-lg border border-slate-100/50"
        >
          <input
            type="text"
            placeholder="Search keywords..."
            value={filters.searchQuery}
            onChange={(e) => handleSearchChange("searchQuery", e.target.value)}
            className="w-full bg-transparent text-slate-800 text-sm placeholder-slate-400 focus:outline-none"
          />
        </form>

        {/* Action Button: "Search" */}
        <button
          onClick={applyFilters}
          className="h-[46px] px-4 flex items-center gap-1.5 bg-bnp-cyan text-white rounded-lg active:scale-95 transition-all text-sm font-bold shadow-sm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            viewBox="0 0 24 24"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <span>search</span>
        </button>

        {/* Apply Filter Button (Mobile Drawer Trigger) */}
        <button
          onClick={() => setIsMobileOpen(true)}
          className="w-[46px] h-[46px] flex items-center justify-center bg-slate-50 border border-slate-200 hover:bg-slate-100 text-slate-700 rounded-lg transition-colors active:scale-95"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <line x1="4" y1="21" x2="4" y2="14" />
            <line x1="4" y1="10" x2="4" y2="3" />
            <line x1="12" y1="21" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12" y2="3" />
            <line x1="20" y1="21" x2="20" y2="16" />
            <line x1="20" y1="12" x2="20" y2="3" />
            <line x1="1" y1="14" x2="7" y2="14" />
            <line x1="9" y1="8" x2="15" y2="8" />
            <line x1="17" y1="16" x2="23" y2="16" />
          </svg>
        </button>
      </div>

      {/* ========================================== */}
      {/* ACTIVE FILTER PILLS                        */}
      {/* ========================================== */}
      <PropertyResultsHeader onApply={onApply} />
      {/* ========================================== */}
      {/* MOBILE FILTER SIDE DRAWER SLIDE-UP         */}
      {/* ========================================== */}
      {isMobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex flex-col justify-end">
          {/* Backdrop overlay trigger */}
          <div
            onClick={() => setIsMobileOpen(false)}
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300"
          />

          {/* Actual Drawer Component layout */}
          <div className="relative bg-white rounded-t-2xl w-full max-h-[85vh] flex flex-col shadow-2xl z-10 animate-slideUp">
            {/* Header section with Reset toggle */}
            <div className="flex flex-col items-center pt-3 pb-3 border-b border-slate-100 px-4">
              <div className="w-12 h-1 bg-slate-200 rounded-full mb-3" />
              <div className="w-full flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setIsMobileOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-700"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
                <h3 className="text-base font-bold text-slate-900">Filters</h3>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-sm font-semibold text-bnp-cyan active:scale-95 transition-all"
                >
                  Clear all
                </button>
              </div>
            </div>

            {/* Scrollable Container Body */}
            <div className="overflow-y-auto p-5 space-y-5 flex-1 pb-24">
              {/* Location Input Box */}
              <div className="flex flex-col gap-1.5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Location
                </p>
                <FormInput
                  type="text"
                  name="location"
                  value={filters.location}
                  placeholder="Enter city, state or area..."
                  onChange={(e) =>
                    handleFilterChange("location", e.target.value)
                  }
                />
              </div>

              {/* Listing Type Dropdown */}
              <div className="flex flex-col gap-1.5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Listing Type
                </p>
                <DropdownMenu
                  name="listingType"
                  value={
                    filters.listingType
                      ? LISTING_TYPE_LABELS[filters.listingType as string] ||
                        capitalizeFirstLetter(filters.listingType as string)
                      : "Any"
                  }
                  onChange={(e) => handleListingTypeChange(e.target.value)}
                  options={["Any", ...LISTING_TYPE_OPTIONS.map((o) => o.label)]}
                />
              </div>

              {/* Property Category Dropdown */}
              <div className="flex flex-col gap-1.5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Property Category
                </p>
                <DropdownMenu
                  name="propertyCategory"
                  value={
                    filters.propertyCategory
                      ? PROPERTY_CATEGORY_LABELS[
                          filters.propertyCategory as string
                        ] || filters.propertyCategory
                      : "Any"
                  }
                  onChange={(e) => handlePropertyCategoryChange(e.target.value)}
                  options={["Any", ...Object.values(PROPERTY_CATEGORY_LABELS)]}
                />
              </div>

              {/* Property Type Dropdown */}
              <div className="flex flex-col gap-1.5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Property Type
                </p>
                <DropdownMenu
                  name="propertyType"
                  value={
                    filters.propertyType
                      ? PropertyTypeLabels[
                          filters.propertyType as keyof typeof PropertyTypeLabels
                        ] || filters.propertyType
                      : "Any"
                  }
                  onChange={(e) => handlePropertyTypeChange(e.target.value)}
                  options={getPropertyTypeOptions(
                    filters.propertyCategory as string,
                  )}
                />
              </div>

              {/* State & City Combination row
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">State</p>
                  <SearchableDropdown
                    name="state"
                    options={["Any", ...nigeriaStates]}
                    value={filters.state || "Any"}
                    onChange={(e) => {
                      handleFilterChange("state", e.target.value === "Any" ? "" : e.target.value);
                      setSelectedState(e.target.value === "Any" ? null : e.target.value);
                      handleFilterChange("city", "Any");
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">City</p>
                  <SearchableDropdown
                    name="city"
                    value={filters.city || "Any"}
                    options={["Any", ...cityOptions]}
                    onChange={(e) => handleFilterChange("city", e.target.value === "Any" ? "" : e.target.value)}
                    disabled={!selectedState}
                  />
                </div>
              </div> */}

              {/* Rooms config row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Bedrooms
                  </p>
                  <DropdownMenu
                    name="bedroomCount"
                    options={[
                      "Any",
                      "1",
                      "2",
                      "3",
                      "4",
                      "5",
                      "6",
                      "7",
                      "8",
                      "9",
                      "10",
                    ]}
                    value={filters.bedroomCount}
                    onChange={(e) =>
                      handleFilterChange("bedroomCount", e.target.value)
                    }
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Bathrooms
                  </p>
                  <DropdownMenu
                    name="bathroomCount"
                    options={[
                      "Any",
                      "1",
                      "2",
                      "3",
                      "4",
                      "5",
                      "6",
                      "7",
                      "8",
                      "9",
                      "10",
                    ]}
                    value={filters.bathroomCount}
                    onChange={(e) =>
                      handleFilterChange("bathroomCount", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="flex flex-col gap-1.5">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Serviced
                  </p>
                  <DropdownMenu
                    name="serviced"
                    value={filters.serviced || "Any"}
                    onChange={(e) =>
                      handleFilterChange(
                        "serviced",
                        e.target.value === "Any" ? "" : e.target.value,
                      )
                    }
                    options={["Any", "Yes", "No"]}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Shared
                  </p>
                  <DropdownMenu
                    name="shared"
                    value={filters.shared || "Any"}
                    onChange={(e) =>
                      handleFilterChange(
                        "shared",
                        e.target.value === "Any" ? "" : e.target.value,
                      )
                    }
                    options={["Any", "Yes", "No"]}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Furnished
                  </p>
                  <DropdownMenu
                    name="furnished"
                    value={filters.furnished || "Any"}
                    onChange={(e) =>
                      handleFilterChange(
                        "furnished",
                        e.target.value === "Any" ? "" : e.target.value,
                      )
                    }
                    options={["Any", "Yes", "No"]}
                  />
                </div>
              </div>

              {/* Budget Configuration Fields */}
              <div className="flex flex-col gap-2">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Price Budget ({selectedCurrency})
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <FormInput
                    name="minPrice"
                    type="text"
                    placeholder="Min budget"
                    value={formatPriceWithCommas(filters.minPrice || "")}
                    onChange={(e) =>
                      handleFilterChange(
                        "minPrice",
                        formatPriceWithCommas(e.target.value),
                      )
                    }
                  />
                  <FormInput
                    name="maxPrice"
                    type="text"
                    placeholder="Max budget"
                    value={formatPriceWithCommas(filters.maxPrice || "")}
                    onChange={(e) =>
                      handleFilterChange(
                        "maxPrice",
                        formatPriceWithCommas(e.target.value),
                      )
                    }
                  />
                </div>
              </div>
            </div>

            {/* Absolute Sticky footer triggers inside Mobile Drawer */}
            <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-slate-100 p-4 flex gap-3 z-20">
              <Button
                onClick={() => setIsMobileOpen(false)}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-semibold h-12 rounded-xl transition-all"
              >
                Cancel
              </Button>
              <Button
                onClick={applyFilters}
                className="flex-1 bg-bnp-cyan hover:bg-[#247A8A] text-white text-sm font-semibold h-12 rounded-xl transition-all shadow-sm"
              >
                Show Listings
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernMinimalistPropertyFilter;
